/**
 * Phone Utility Functions
 *
 * Provides utility functions for formatting and validating phone numbers,
 * with a focus on Australian phone number formats.
 *
 * @module utils/PhoneUtil
 */

/**
 * Format a phone number to Australian format: +61 4XX XXX XXX
 *
 * Handles various input formats:
 * - 0412345678 -> +61 412 345 678
 * - 61412345678 -> +61 412 345 678
 * - +61412345678 -> +61 412 345 678
 * - 412345678 -> +61 412 345 678
 * - +610432286777 -> +61 432 286 777 (removes extra 0 after country code)
 *
 * @param phone - The phone number to format
 * @returns Formatted phone number or original input if invalid
 *
 * @example
 * ```typescript
 * formatAustralianPhone('0412345678') // Returns: '+61 412 345 678'
 * formatAustralianPhone('61412345678') // Returns: '+61 412 345 678'
 * formatAustralianPhone('+61412345678') // Returns: '+61 412 345 678'
 * formatAustralianPhone('+610432286777') // Returns: '+61 432 286 777'
 * ```
 */
export const formatAustralianPhone = (phone: string | null | undefined): string => {
  // Return empty string if phone is null or undefined
  if (!phone) {
    return '';
  }

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Handle different input formats
  let normalizedNumber = digitsOnly;

  // If starts with 61 (country code)
  if (digitsOnly.startsWith('61')) {
    normalizedNumber = digitsOnly;
    // Check if there's an extra 0 after the country code (e.g., 610432286777)
    // Australian mobile numbers should be 61 + 4XX XXX XXX (starting with 4, not 0)
    if (normalizedNumber.length === 12 && normalizedNumber.startsWith('610')) {
      // Remove the extra 0: 610432286777 -> 61432286777
      normalizedNumber = '61' + normalizedNumber.substring(3);
    }
  }
  // If starts with 0 (Australian local format), remove the 0 and add 61
  else if (digitsOnly.startsWith('0')) {
    normalizedNumber = '61' + digitsOnly.substring(1);
  }
  // If doesn't start with 61 or 0, assume it's missing country code
  else {
    normalizedNumber = '61' + digitsOnly;
  }

  // Validate that we have the correct length (61 + 9 digits = 11 total)
  if (normalizedNumber.length !== 11 || !normalizedNumber.startsWith('61')) {
    // Return original input if it doesn't match expected format
    return phone;
  }

  // Format as +61 4XX XXX XXX
  const countryCode = normalizedNumber.substring(0, 2); // '61'
  const firstPart = normalizedNumber.substring(2, 5);   // '4XX'
  const secondPart = normalizedNumber.substring(5, 8);  // 'XXX'
  const thirdPart = normalizedNumber.substring(8, 11);  // 'XXX'

  return `+${countryCode} ${firstPart} ${secondPart} ${thirdPart}`;
};

/**
 * Validate if a phone number is a valid Australian mobile number
 *
 * @param phone - The phone number to validate
 * @returns True if valid Australian mobile number, false otherwise
 *
 * @example
 * ```typescript
 * isValidAustralianMobile('0412345678') // Returns: true
 * isValidAustralianMobile('0312345678') // Returns: false (not mobile)
 * ```
 */
export const isValidAustralianMobile = (phone: string | null | undefined): boolean => {
  if (!phone) {
    return false;
  }

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Normalize to start with 61
  let normalizedNumber = digitsOnly;
  if (digitsOnly.startsWith('0')) {
    normalizedNumber = '61' + digitsOnly.substring(1);
  } else if (!digitsOnly.startsWith('61')) {
    normalizedNumber = '61' + digitsOnly;
  }

  // Check if it's a valid Australian mobile number
  // Must be 11 digits starting with 614 (Australian mobile numbers start with 04)
  return normalizedNumber.length === 11 &&
         normalizedNumber.startsWith('614');
};

/**
 * Strip formatting from a phone number to get digits only
 *
 * @param phone - The phone number to strip
 * @returns Phone number with only digits
 *
 * @example
 * ```typescript
 * stripPhoneFormatting('+61 412 345 678') // Returns: '61412345678'
 * ```
 */
export const stripPhoneFormatting = (phone: string | null | undefined): string => {
  if (!phone) {
    return '';
  }
  return phone.replace(/\D/g, '');
};

/**
 * PhoneUtil class for phone number operations
 * Provides static methods for formatting and validating phone numbers
 */
export class PhoneUtil {
  /**
   * Format a phone number to Australian format
   * @see formatAustralianPhone
   */
  static formatAustralian(phone: string | null | undefined): string {
    return formatAustralianPhone(phone);
  }

  /**
   * Validate if a phone number is a valid Australian mobile number
   * @see isValidAustralianMobile
   */
  static isValidAustralianMobile(phone: string | null | undefined): boolean {
    return isValidAustralianMobile(phone);
  }

  /**
   * Strip formatting from a phone number
   * @see stripPhoneFormatting
   */
  static stripFormatting(phone: string | null | undefined): string {
    return stripPhoneFormatting(phone);
  }
}